import React, { useEffect, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import './style.scss';

/**
 * Confirmation de suppression d'un partenaire — base neuve.
 *
 * Deux niveaux d'exigence :
 *   - strict = false (configuration en cours) : simple confirmation.
 *   - strict = true  (liaison configurée)     : case à cocher + saisie du nom
 *     du partenaire, comme la suppression d'un dépôt GitHub. Une seule action
 *     délibérée, impossible à enchaîner par réflexe.
 *
 * @param {Boolean} props.open
 * @param {String}  props.partnerName nom affiché / attendu à la saisie
 * @param {Boolean} props.strict
 * @param {Function} props.onClose
 * @param {Function} props.onConfirm
 */
const DeletePartnerDialog = ({
	open,
	partnerName = '',
	strict = false,
	onClose,
	onConfirm,
}) => {
	const [acknowledged, setAcknowledged] = useState(false);
	const [typedName, setTypedName] = useState('');

	useEffect(() => {
		if (!open) {
			setAcknowledged(false);
			setTypedName('');
		}
	}, [open]);

	const expected = partnerName.trim();
	const nameMatches =
		!!expected && typedName.trim().toLowerCase() === expected.toLowerCase();
	const canConfirm = !strict || (acknowledged && nameMatches);

	const confirmAndClose = () => {
		onClose();
		onConfirm();
	};

	return (
		<Dialog open={open} onClose={onClose} className="delete-partner">
			<DialogTitle className="delete-partner__title">
				<WarningIcon className="delete-partner__title-icon" />
				{I18n.t(
					strict ? 'partner.delete.title_binding' : 'partner.delete.title_config',
					{ partner: partnerName }
				)}
			</DialogTitle>

			<DialogContent className="delete-partner__content">
				<p>
					{I18n.t(
						strict
							? 'partner.delete.binding_intro'
							: 'partner.delete.config_intro',
						{ partner: partnerName }
					)}
				</p>

				{strict ? (
					<>
						<ul className="delete-partner__effects">
							<li>
								{I18n.t('partner.delete.effect_exchanges', {
									partner: partnerName,
								})}
							</li>
							<li>{I18n.t('partner.delete.effect_settings')}</li>
							<li>{I18n.t('partner.delete.effect_reconfigure')}</li>
						</ul>

						<label className="delete-partner__checkbox">
							<input
								type="checkbox"
								checked={acknowledged}
								onChange={event => setAcknowledged(event.target.checked)}
							/>
							<span>{I18n.t('partner.delete.acknowledge')}</span>
						</label>

						<p className="delete-partner__type-label">
							{I18n.t('partner.delete.type_to_confirm')}{' '}
							<strong>{expected}</strong>
						</p>
						<input
							className="delete-partner__input"
							type="text"
							autoComplete="off"
							placeholder={expected}
							value={typedName}
							onChange={event => setTypedName(event.target.value)}
						/>
					</>
				) : null}
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose} className="button-cancel">
					{I18n.t('general.cancel')}
				</Button>
				<Button
					color="primary"
					variant="contained"
					disabled={!canConfirm}
					onClick={confirmAndClose}>
					{I18n.t('general.delete')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DeletePartnerDialog;
